import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { api, type CommandType } from '../lib/api'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { polling } from '../lib/polling'
import { isPublicPathname } from '../lib/route-paths'
import { type OperationStep } from '../components/device-operation'

const STORAGE_KEY_PREFIX = 'unbreakable_active_jobs'
const LEGACY_STORAGE_KEY = 'unbreakable_active_jobs'

function storageKeyForUser(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`
}

/** @deprecated Use OperationStep instead */
export type AddDeviceStep = OperationStep

export interface ActiveJob {
  commandId: string
  type: CommandType
  deviceId: string | null
  deviceName?: string
  udid: string | null
  createdAt: string
  currentStep?: OperationStep
}

interface ActiveJobsContextValue {
  activeJobs: ActiveJob[]
  addJob: (job: ActiveJob) => void
  updateJob: (commandId: string, updates: Partial<ActiveJob>) => void
  removeJob: (commandId: string) => void
  isLoading: boolean
  selectedJobId: string | null
  setSelectedJobId: (id: string | null) => void
}

const ActiveJobsContext = createContext<ActiveJobsContextValue | null>(null)

export const useActiveJobs = () => {
  const context = useContext(ActiveJobsContext)
  if (!context) {
    throw new Error('useActiveJobs must be used within an ActiveJobsProvider')
  }
  return context
}

const noopJobs: ActiveJobsContextValue = {
  activeJobs: [],
  addJob: () => {},
  updateJob: () => {},
  removeJob: () => {},
  isLoading: false,
  selectedJobId: null,
  setSelectedJobId: () => {},
}

/** Safe version that returns no-op stubs when outside ActiveJobsProvider (e.g. school routes) */
export const useActiveJobsOptional = (): ActiveJobsContextValue => {
  const context = useContext(ActiveJobsContext)
  return context ?? noopJobs
}

interface ActiveJobsProviderProps {
  children: ReactNode
}

export const ActiveJobsProvider = ({ children }: ActiveJobsProviderProps) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)

  const isPublicPage = pathname ? isPublicPathname(pathname) || pathname.startsWith('/landing') : false
  const { data: currentUser, isLoading: userLoading } = useCurrentUser({ enabled: !isPublicPage })

  const scopedKey = currentUser?.id ? storageKeyForUser(currentUser.id) : null

  // Ref to read current activeJobs in polling without causing effect cascade
  const activeJobsRef = useRef(activeJobs)
  useEffect(() => { activeJobsRef.current = activeJobs }, [activeJobs])

  // Guard: only run full init when user identity changes, not on every navigation
  const prevUserIdRef = useRef<string | null>(null)
  const hasInitializedRef = useRef(false)

  // Re-sync from backend when tab becomes visible after being hidden
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        hasInitializedRef.current = false
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [])

  useEffect(() => {
    const initializeJobs = async () => {
      if (isPublicPage) {
        setIsLoading(false)
        setIsAuthenticated(false)
        return
      }
      if (userLoading) {
        return
      }
      if (!currentUser || !scopedKey) {
        setIsLoading(false)
        setIsAuthenticated(false)
        hasInitializedRef.current = false
        prevUserIdRef.current = null
        return
      }

      // Skip re-init if same user and already initialized (e.g. just navigated between pages)
      const userChanged = currentUser.id !== prevUserIdRef.current
      if (hasInitializedRef.current && !userChanged) {
        setIsAuthenticated(true)
        return
      }

      prevUserIdRef.current = currentUser.id
      setIsAuthenticated(true)
      setIsLoading(true)

      let storedJobs: ActiveJob[] = []
      try {
        const stored = localStorage.getItem(scopedKey)
        if (stored) {
          storedJobs = JSON.parse(stored)
        } else {
          const legacy = localStorage.getItem(LEGACY_STORAGE_KEY)
          if (legacy) {
            storedJobs = JSON.parse(legacy)
            localStorage.removeItem(LEGACY_STORAGE_KEY)
          }
        }
      } catch {
        // Ignore parse errors
      }

      try {
        const response = await api.getActiveCommands()
        const backendJobs = response.commands

        const backendJobIds = new Set(backendJobs.map((cmd) => cmd.id))
        const validStoredJobs = storedJobs.filter((job) => backendJobIds.has(job.commandId))

        const storedJobIds = new Set(validStoredJobs.map((job) => job.commandId))
        const newJobsFromBackend: ActiveJob[] = backendJobs
          .filter((cmd) => !storedJobIds.has(cmd.id))
          .map((cmd) => ({
            commandId: cmd.id,
            type: cmd.type,
            deviceId: cmd.deviceId,
            udid: cmd.udid,
            createdAt: cmd.createdAt,
            currentStep: (cmd.type === 'supervise' || cmd.type === 'android_setup') ? 'progress' as OperationStep : undefined,
          }))

        const migratedStoredJobs = validStoredJobs.map((job) => ({
          ...job,
          currentStep: (job.type === 'supervise' || job.type === 'android_setup') && !job.currentStep ? 'progress' as OperationStep : job.currentStep,
        }))

        const mergedJobs = [...migratedStoredJobs, ...newJobsFromBackend]
        setActiveJobs(mergedJobs)
        localStorage.setItem(scopedKey, JSON.stringify(mergedJobs))
      } catch {
        setActiveJobs(storedJobs)
      }

      hasInitializedRef.current = true
      setIsLoading(false)
    }

    initializeJobs()
  }, [isPublicPage, currentUser, userLoading, scopedKey])

  useEffect(() => {
    if (!isLoading && scopedKey) {
      localStorage.setItem(scopedKey, JSON.stringify(activeJobs))
    }
  }, [activeJobs, isLoading, scopedKey])

  useEffect(() => {
    if (isPublicPage || !isAuthenticated || isLoading) {
      return
    }

    let isPolling = false
    const pollCommandStatuses = async () => {
      const currentJobs = activeJobsRef.current
      if (isPolling || currentJobs.length === 0) return
      isPolling = true
      const jobsToCheck = [...currentJobs]
      const jobsToRemove = new Set<string>()

      try {
        await Promise.all(
          jobsToCheck.map(async (job) => {
            try {
              const command = await api.getCommandStatus(job.commandId)
              if (command.status === 'completed' || command.status === 'failed') {
                jobsToRemove.add(job.commandId)
              }
            } catch {
              // Command may have been cleaned up by deferred deletion — treat as complete
              jobsToRemove.add(job.commandId)
            }
          })
        )
      } finally {
        isPolling = false
      }

      if (jobsToRemove.size > 0) {
        setActiveJobs((prev) => prev.filter((job) => !jobsToRemove.has(job.commandId)))
        setSelectedJobId((prev) => (prev && jobsToRemove.has(prev) ? null : prev))
      }
    }

    const interval = setInterval(pollCommandStatuses, polling.activeJobStatus)
    pollCommandStatuses()

    return () => clearInterval(interval)
  }, [isLoading, isAuthenticated, isPublicPage])

  const addJob = useCallback((job: ActiveJob) => {
    setActiveJobs((prev) => {
      if (prev.some((j) => j.commandId === job.commandId)) {
        return prev
      }
      return [...prev, job]
    })
  }, [])

  const updateJob = useCallback((commandId: string, updates: Partial<ActiveJob>) => {
    setActiveJobs((prev) =>
      prev.map((job) =>
        job.commandId === commandId ? { ...job, ...updates } : job
      )
    )
  }, [])

  const removeJob = useCallback((commandId: string) => {
    setActiveJobs((prev) => prev.filter((job) => job.commandId !== commandId))
    setSelectedJobId((prev) => (prev === commandId ? null : prev))
  }, [])

  const value = useMemo(() => ({
    activeJobs,
    addJob,
    updateJob,
    removeJob,
    isLoading,
    selectedJobId,
    setSelectedJobId,
  }), [activeJobs, addJob, updateJob, removeJob, isLoading, selectedJobId])

  return (
    <ActiveJobsContext.Provider value={value}>
      {children}
    </ActiveJobsContext.Provider>
  )
}
